import type {
  DealerOptionItemDto,
  FactoryOptionDto,
  MachineAttachmentItemDto,
  MachineQuotationItemDto
} from '@api/machines'
import {
  formatPriceAndCurrency,
  getDealerFitName,
  splitFactoryOptionsItems
} from '@common/utils'
import {
  MolsonAccordion,
  MolsonArchived,
  MolsonBox,
  MolsonCheckbox,
  MolsonGrid,
  MolsonRadioButton,
  MolsonRadioGroup,
  MolsonTypography
} from '@components/reusable'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArchivedWrapper } from '../archived-wrapper/ArchivedWrapper'

export type EquipmentOptionsSectionProps = {
  expanded?: boolean
  machineQuotation: MachineAttachmentItemDto | MachineQuotationItemDto
  translationPrefix: string
}

const EquipmentOptionsSection = ({
  expanded = false,
  machineQuotation,
  translationPrefix
}: EquipmentOptionsSectionProps) => {
  const [isSectionExpanded, setIsSectionExpanded] = useState<boolean>(expanded)
  const { factoryOptionItems } = machineQuotation

  const { dealerOptions } = machineQuotation as MachineQuotationItemDto

  const itemsWithFamily = splitFactoryOptionsItems(
    factoryOptionItems ?? [],
    true
  )
  const itemsWithoutFamily = splitFactoryOptionsItems(
    factoryOptionItems ?? [],
    false
  )

  const { t } = useTranslation('', {
    keyPrefix: translationPrefix
  })

  const generateDealerOptionValue = (
    dealerOption: DealerOptionItemDto
  ): string => {
    return `${getDealerFitName(dealerOption)} (${formatPriceAndCurrency(
      (dealerOption.dnp ?? 0) * (dealerOption.quantity ?? 0)
    )})`
  }

  return (
    <MolsonAccordion
      withTooltip={{
        description: t('unitSpecificationAmendments'),
        title: t('equipmentOptions')
      }}
      expanded={isSectionExpanded}
      onChange={() => setIsSectionExpanded(prev => !prev)}
      primaryText={t('equipmentOptions')}
    >
      <MolsonBox sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {factoryOptionItems && factoryOptionItems.length > 0 && (
          <MolsonBox>
            <MolsonTypography sx={{ mb: 3.75 }} variant="h4">
              {t('factoryFit')}
            </MolsonTypography>
            {(itemsWithFamily?.length || 0) > 0 && (
              <MolsonGrid
                container={true}
                mb={(itemsWithoutFamily?.length || 0) > 0 ? 1 : 0}
                spacing={1}
              >
                {itemsWithFamily?.map((factoryOptionItem: FactoryOptionDto) => (
                  <MolsonGrid item={true} key={factoryOptionItem.id} xs={6}>
                    {factoryOptionItem.isArchived ? (
                      <ArchivedWrapper>
                        <MolsonArchived
                          label={`${
                            factoryOptionItem.name
                          } (${formatPriceAndCurrency(factoryOptionItem.dnp)})`}
                          outerBoxSx={{
                            pb: 0,
                            pt: 0
                          }}
                          checked={true}
                          disabled={true}
                          hasRadioButton={true}
                          key={factoryOptionItem.id}
                        />
                      </ArchivedWrapper>
                    ) : (
                      <MolsonRadioGroup
                        value={`${
                          factoryOptionItem.name
                        } (${formatPriceAndCurrency(factoryOptionItem.dnp)})`}
                      >
                        <MolsonRadioButton
                          formControlLabelProps={{
                            label: `${
                              factoryOptionItem.name
                            } (${formatPriceAndCurrency(
                              factoryOptionItem.dnp
                            )})`
                          }}
                          radioBtnProps={{
                            disabled: true,
                            value: `${
                              factoryOptionItem.name
                            } (${formatPriceAndCurrency(
                              factoryOptionItem.dnp
                            )})`
                          }}
                          checked={true}
                        />
                      </MolsonRadioGroup>
                    )}
                  </MolsonGrid>
                ))}
              </MolsonGrid>
            )}
            {(itemsWithoutFamily?.length || 0) > 0 && (
              <MolsonGrid container={true} spacing={1}>
                {itemsWithoutFamily?.map(
                  (factoryOptionItem: FactoryOptionDto) => (
                    <MolsonGrid item={true} key={factoryOptionItem.id} xs={6}>
                      {factoryOptionItem.isArchived ? (
                        <ArchivedWrapper>
                          <MolsonArchived
                            label={`${
                              factoryOptionItem.name
                            } (${formatPriceAndCurrency(
                              factoryOptionItem.dnp
                            )})`}
                            outerBoxSx={{
                              pb: 1,
                              pt: 1
                            }}
                            checked={true}
                            disabled={true}
                            key={factoryOptionItem.id}
                          />
                        </ArchivedWrapper>
                      ) : (
                        <MolsonCheckbox
                          secondLabel={`
                            (${formatPriceAndCurrency(factoryOptionItem.dnp)})`}
                          checked={true}
                          disabled={true}
                          label={factoryOptionItem.name ?? undefined}
                        />
                      )}
                    </MolsonGrid>
                  )
                )}
              </MolsonGrid>
            )}
          </MolsonBox>
        )}
        {dealerOptions && dealerOptions.length > 0 && (
          <MolsonBox>
            <MolsonTypography sx={{ mb: 3.75 }} variant="h4">
              {t('dealerFit')}
            </MolsonTypography>

            <MolsonGrid container={true} mt={2} spacing={1}>
              <MolsonBox
                display="grid"
                gap={2}
                gridTemplateColumns="repeat(2, 1fr)"
                paddingX={1}
              >
                {dealerOptions.map((dealerOption: DealerOptionItemDto) => (
                  <Fragment key={dealerOption.id}>
                    {dealerOption.isArchived ? (
                      <ArchivedWrapper>
                        <MolsonArchived
                          secondLabel={`(${formatPriceAndCurrency(
                            dealerOption.dnp as number
                          )})`}
                          checked={true}
                          hasRadioButton={true}
                          key={dealerOption.id}
                          label={`${dealerOption.type} ${dealerOption.subType1} ${dealerOption.subType2} ${dealerOption.family}`}
                        />
                      </ArchivedWrapper>
                    ) : (
                      <MolsonRadioGroup
                        value={generateDealerOptionValue(dealerOption)}
                      >
                        <MolsonRadioButton
                          formControlLabelProps={{
                            label: generateDealerOptionValue(dealerOption)
                          }}
                          radioBtnProps={{
                            disabled: true,
                            value: generateDealerOptionValue(dealerOption)
                          }}
                          checked={true}
                        />
                      </MolsonRadioGroup>
                    )}
                  </Fragment>
                ))}
              </MolsonBox>
            </MolsonGrid>
          </MolsonBox>
        )}
      </MolsonBox>
    </MolsonAccordion>
  )
}

export { EquipmentOptionsSection }
